// src/pages/AdminPagesBannersPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import Loader from "../layouts/components/Loader";
import toast from "react-hot-toast";
import "./AdminPagesBannersPage.css";

const API_BASE = "https://api.mariamlyluxe.com/public/api";

export default function AdminPagesBannersPage() {
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingBanner, setDeletingBanner] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    page_name: "",
    title: "",
    image: null,
    video: null,
    media_type: "image",
    is_active: true,
  });

  // Available page names
  const pageNames = [
    { value: "brands", label: "Brands" },
    { value: "shop", label: "Shop" },
    { value: "product", label: "Product" },
    { value: "offers", label: "Offers" },
    { value: "contact", label: "Contact" }
  ];

  // ===== Helpers =====
  const getToken = () =>
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    "";

  const resolveImageUrl = (item) => {
    if (!item) return null;
    if (typeof item.image_url === "string" && item.image_url) return item.image_url;
    if (typeof item.image_path === "string" && item.image_path) return item.image_path;
    if (typeof item.image === "string" && item.image) return item.image;
    if (item.image && typeof item.image === "object") {
      if (item.image.url) return item.image.url;
      if (item.image.path)
        return `${API_BASE.replace("/api", "")}/storage/${item.image.path}`;
    }
    return null;
  };

  useEffect(() => {
    const token = getToken();
    const userRole = localStorage.getItem("user_role");

    if (!token || userRole !== "1") {
      navigate("/");
      return;
    }

    fetchBanners(token);
  }, [navigate]);

  const fetchBanners = async (token) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/admin/pages-banners`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Accept several shapes:
        // { success: true, banners: [...] } (pages-banners format)
        // { data: { data: [...] } } (Laravel paginator nested)
        // { data: [...] }
        // [] bare array
        const list = Array.isArray(data?.banners)
          ? data.banners
          : Array.isArray(data?.data?.data)
          ? data.data.data
          : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
          ? data
          : [];
        setBanners(list);
      } else {
        toast.error("Failed to fetch page banners");
        setBanners([]);
      }
    } catch (error) {
      toast.error("Error fetching page banners");
      console.error("Error:", error);
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? checked :
        type === "file" ? files[0] :
        value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = getToken();
    setIsSubmitting(true);

    // 1) Client-side guards
    const isEdit = Boolean(editingBanner);

    // page_name and image required
    if (!formData.page_name) {
      toast.error("Page name is required");
      setIsSubmitting(false);
      return;
    }

    if (!isEdit && !formData.image) {
      toast.error("Banner image is required");
      setIsSubmitting(false);
      return;
    }

    // 2) Build FormData
    const fd = new FormData();
    fd.append("page_name", formData.page_name);
    fd.append("title", formData.title || "");
    if (formData.image) fd.append("image", formData.image);
    fd.append("is_active", formData.is_active ? "1" : "0");

    const url = isEdit
      ? `${API_BASE}/admin/pages-banners/${editingBanner.id}`
      : `${API_BASE}/admin/pages-banners`;

    // Spoof PUT on edit (safer with multipart on many PHP hosts)
    if (isEdit) fd.append("_method", "PUT");

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        body: fd,
      });

      if (response.ok) {
        toast.success(isEdit ? "Page banner updated successfully" : "Page banner created successfully");
        setShowModal(false);
        setEditingBanner(null);
        resetForm();
        fetchBanners(token);
      } else {
        const errorData = await response.json().catch(() => ({}));
        // Show first validation error if present
        if (errorData?.errors && typeof errorData.errors === "object") {
          const firstKey = Object.keys(errorData.errors)[0];
          const firstMsg = errorData.errors[firstKey]?.[0];
          toast.error(firstMsg || errorData.message || "Failed to save page banner");
        } else {
          toast.error(errorData.message || "Failed to save page banner");
        }
        console.error("Save error detail:", errorData);
      }
    } catch (error) {
      toast.error("Error saving page banner");
      console.error("Error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      page_name: banner.page_name || "",
      title: banner.title || "",
      image: null, // don't preload file
      is_active: Boolean(banner?.is_active),
    });
    setShowModal(true);
  };

  const handleDelete = async (bannerId) => {
    const banner = banners.find(b => b.id === bannerId);
    setDeletingBanner(banner);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingBanner) return;
    
    setIsDeleting(true);
    const token = getToken();

    try {
      const response = await fetch(`${API_BASE}/admin/pages-banners/${deletingBanner.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (response.ok) {
        toast.success("Page banner deleted successfully");
        setShowDeleteModal(false);
        setDeletingBanner(null);
        fetchBanners(token);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.message || "Failed to delete page banner");
      }
    } catch (error) {
      toast.error("Error deleting page banner");
      console.error("Error:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingBanner(null);
    setIsDeleting(false);
  };

  const resetForm = () => {
    setFormData({
      page_name: "",
      title: "",
      image: null,
      is_active: true,
    });
  };

  const openModal = () => {
    setEditingBanner(null);
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBanner(null);
    resetForm();
  };

  const handleToggleStatus = async (banner) => {
    const token = getToken();
    try {
      const response = await fetch(`${API_BASE}/admin/pages-banners/${banner.id}/toggle-status`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (response.ok) {
        toast.success(`Page banner ${banner.is_active ? 'deactivated' : 'activated'} successfully`);
        fetchBanners(token);
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.message || "Failed to toggle page banner status");
      }
    } catch (error) {
      toast.error("Error toggling page banner status");
      console.error("Error:", error);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-pages-banners">
          <div className="loading">Loading page banners...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-pages-banners">
        {/* Header */}
        <div className="admin-header">
          <div className="admin-title">
            <h1>Pages Banners Management</h1>
            <p>Manage banners for different pages of the website</p>
          </div>
          {/* <div className="admin-actions">
            <button className="btn-primary" type="button" onClick={openModal}>
              + Add New Page Banner
            </button>
          </div> */}
        </div>

        {/* Banners Grid */}
        <div className="banners-grid">
          {banners.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <svg viewBox="0 0 24 24">
                  <path
                    d="M3 4a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v2.586a1 1 0 0 1-.293.707l-6.414 6.414a1 1 0 0 0-.293.707V17l-4 4v-6.586a1 1 0 0 0-.293-.707L3.293 7.293A1 1 0 0 1 3 6.586V4z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
              </div>
              <h3>No page banners found</h3>
              <p>Create your first page banner to get started</p>
              {/* <button className="btn-primary" type="button" onClick={openModal}>
                + Add New Page Banner
              </button> */}
            </div>
          ) : (
            banners.map((banner) => {
              const imgUrl = resolveImageUrl(banner);
              const pageLabel = pageNames.find(p => p.value === banner.page_name)?.label || banner.page_name;
              return (
                <div key={banner.id} className="banner-card">
                  <div className="banner-image">
                    {imgUrl ? (
                      <img
                        src={imgUrl}
                        alt={banner.title || pageLabel}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          if (e.currentTarget.nextSibling) {
                            e.currentTarget.nextSibling.style.display = "flex";
                          }
                        }}
                      />
                    ) : null}
                    <div
                      className="image-placeholder"
                      style={{ display: imgUrl ? "none" : "flex" }}
                    >
                      <svg viewBox="0 0 24 24">
                        <path
                          d="M3 4a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v2.586a1 1 0 0 1-.293.707l-6.414 6.414a1 1 0 0 0-.293.707V17l-4 4v-6.586a1 1 0 0 0-.293-.707L3.293 7.293A1 1 0 0 1 3 6.586V4z"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                      </svg>
                    </div>
                    <div className={`banner-status ${banner.is_active ? 'active' : 'inactive'}`}>
                      {banner.is_active ? 'Active' : 'Inactive'}
                    </div>
                  </div>

                  <div className="banner-info">
                    <div className="banner-details">
                      <div className="banner-title">
                        <span className="label">Page:</span>
                        <span className="value">{pageLabel}</span>
                      </div>
                      {banner.title && (
                        <div className="banner-subtitle">
                          <span className="label">Title:</span>
                          <span className="value">{banner.title}</span>
                        </div>
                      )}
                      <div className="banner-id">
                        <span className="label">ID:</span>
                        <span className="value">#{banner.id}</span>
                      </div>
                      <div className="banner-date">
                        <span className="label">Created:</span>
                        <span className="value">
                          {banner.created_at
                            ? new Date(banner.created_at).toLocaleDateString()
                            : "-"}
                        </span>
                      </div>
                    </div>

                     <div className="banner-actions">
                       <button
                         className="btn-edit"
                         onClick={() => handleEdit(banner)}
                         title="Edit Page Banner"
                       >
                         <svg viewBox="0 0 24 24">
                           <path
                             d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                             fill="none"
                             stroke="currentColor"
                             strokeWidth="2"
                           />
                           <path
                             d="M18.5 2.5a2 2 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                             fill="none"
                             stroke="currentColor"
                             strokeWidth="2"
                           />
                         </svg>
                         Edit
                       </button>
                     </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingBanner ? "Edit Page Banner" : "Add New Page Banner"}</h2>
                <button className="modal-close" onClick={closeModal}>
                  <svg viewBox="0 0 24 24">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="modal-form">
                <div className="form-group">
                  <label htmlFor="page_name">Page Name *</label>
                  <select
                    id="page_name"
                    name="page_name"
                    value={formData.page_name}
                    onChange={handleInputChange}
                    required
                    disabled={!!editingBanner}
                  >
                    <option value="">Select a page</option>
                    {pageNames.map(page => (
                      <option key={page.value} value={page.value}>
                        {page.label}
                      </option>
                    ))}
                  </select>
                  {editingBanner && (
                    <small className="form-help">
                      Page name cannot be changed after creation
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="title">Title (Optional)</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Banner title"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="image">Banner Image {!editingBanner && '*'}</label>
                  <input
                    type="file"
                    id="image"
                    name="image"
                    onChange={handleInputChange}
                    accept="image/*"
                    required={!editingBanner}
                  />
                  {editingBanner && resolveImageUrl(editingBanner) && (
                    <div className="current-image">
                      <p>Current image:</p>
                      <img src={resolveImageUrl(editingBanner)} alt="Current" />
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                    />
                    <span>Active Banner</span>
                  </label>
                  <small className="form-help">
                    Only active banners will be displayed on the website
                  </small>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader size={16} strokeWidth={2} />
                        {editingBanner ? "Updating..." : "Creating..."}
                      </>
                    ) : (
                      editingBanner ? "Update Page Banner" : "Create Page Banner"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </AdminLayout>
  );
}
